import { ReactElement, ReactNode, useEffect, useMemo, useRef } from 'react';

import styled from 'styled-components';

import {
  ActionCard,
  ActionCardContent,
  ActionCardDescription,
  ActionCardHeader,
} from '@components/action-card';
import { AssignList } from '@components/assign-button';
import { Button } from '@components/button';
import { createForm, useCreateFormStore, useFormField } from '@components/form';
import { FormDataType, FormInstance } from '@components/form/create-form';
import { FieldType as FormFieldType } from '@components/form/v2/types';
import { IconButton, IconButtonProps } from '@components/icon-button';
import { IconProps } from '@components/icons';
import { IconApplicants } from '@components/icons/applicants';
import { IconAssign } from '@components/icons/assign';
import { BaseIcon } from '@components/icons/base';
import { IconCompany } from '@components/icons/company';
import { IconCopy } from '@components/icons/copy';
import { IconForm } from '@components/icons/form';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconPersonDetails } from '@components/icons/icon-person-details';
import { IconId } from '@components/icons/id';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconOpenPreview } from '@components/icons/open-preview';
import { IconPerson } from '@components/icons/person';
import { IconTextFormatLink } from '@components/icons/text-format-link';
import { IconTrash } from '@components/icons/trash';
import { IconTrigger } from '@components/icons/trigger';
import { RelativeSpinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { hideModal } from '@components/modal';
import { useRotatingLoadingMessages } from '@components/rotating-loading-messages/use-rotating-loading-messages';
import {
  hideSidebar,
  Sidebar,
  SimpleHeaderProps,
  useIsSidebarActive,
  useSidebarState,
} from '@components/sidebar';
import { Stack } from '@components/stack';
import { StatusIndicator, StatusIndicatorProps } from '@components/status-indicator';
import {
  FieldMappingFieldItem,
  FieldMappingGroup,
  FieldType,
} from '@components/text-editors/addons/field-mapping';
import { fieldMappingIconColor } from '@components/text-editors/addons/field-mapping-type-maps';
import { TextAreaDynamic } from '@components/textarea/textarea-dynamic';
import { useScopedTranslate } from '@locale/translate';
import { Color, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { XOR } from '@utils/types';

import { useCompanyTravellerAttributeTemplateVariables } from 'api/attributes/hooks';
import { useDuplicateJourneyAction } from 'api/journey-actions/hooks';
import { useJourneyFormsList } from 'api/journey-forms/hooks';
import { JourneyForm } from 'api/journey-forms/requests';
import { useJourney } from 'api/journeys/hooks';
import { ACTION_TYPE, ActionVariable, BASE_ACTION_TYPE, JourneyAction } from 'api/types';
import { getActionIcon, getActionIconColor, getActionLabel } from 'ui/actions/action-config';
import { ActionIcon } from 'ui/actions/action-config';
import { ActionType } from 'ui/actions/calendar-event/common';
import { TravellerJourneyNode } from 'ui/traveller-journey-builder/node-types/types';

import { JourneyNode } from './types';

import { useDeleteJourneyAction } from '../hooks/use-delete-journey-action';
import { useJourneyBuilderActionsContext } from '../journey-builder-actions-context';
import { MAX_VISIBLE_PRESENCE_AVATARS } from '../presence/constants';
import { usePresence } from '../presence/presence-context';
import { UserAvatarList } from '../presence/user-avatar-list';

const AssigneesContainer = styled.div({ display: 'flex' });

type AssigneesProps = Partial<StatusIndicatorProps> & {
  assignees: JourneyNode['data']['assignees'];
};

export const Assignees = ({
  assignees = [],
  variant = 'notStarted',
  ratio,
  icon = <IconPerson />,
  tooltip,
  ...rest
}: AssigneesProps) => {
  return (
    <AssigneesContainer {...rest}>
      <AssignList>
        <div className="assignee-list">
          {assignees.length === 0 ? (
            <AssignList.Icon icon={<IconAssign />} />
          ) : (
            assignees.map((user) => <AssignList.Avatar key={user.name} assignee={user} />)
          )}
        </div>
        <div style={{ zIndex: 1 }}>
          <StatusIndicator variant={variant} icon={icon} ratio={ratio} tooltip={tooltip} />
        </div>
      </AssignList>
    </AssigneesContainer>
  );
};

const Banners = ({ node }: { node: JourneyNode }) => (
  <>
    {node.data?.isAutomatic ? (
      <StatusIndicator variant="notStarted" icon={<IconTrigger />} />
    ) : (
      <Assignees assignees={node.data?.assignees} />
    )}
  </>
);

export type SidebarProps = {
  journeyActionId: string;
  isNewAction?: boolean;
};

export const NodeActions = ({
  node,
  customExecutionIndicators,
}: {
  node: JourneyNode;
  customExecutionIndicators?: React.ReactNode;
}) => {
  const { t } = useScopedTranslate(variableLocale);
  const actionBasic = nodeToActionBasic(node);
  const { handleDuplicate, handleDelete } = useJourneyBuilderActionsContext();

  return (
    <>
      {!node.data?.isConfigured && <IconFormIndicatorWarning />}
      {customExecutionIndicators ?? <Banners node={node} />}
      <div className="node-actions">
        <Menu
          buttonProps={{
            variant: 'ghost',
            iconRight: <IconMoreVertical />,
          }}
        >
          <Menu.Item
            label={t('core.duplicate')}
            onSelect={() => handleDuplicate(node.id)}
            icon={<IconCopy />}
          />
          <Menu.Item
            label={t('core.delete')}
            variant="danger"
            onSelect={() => handleDelete(actionBasic)}
            icon={<IconTrash />}
          />
        </Menu>
      </div>
    </>
  );
};

export function useRawVariableGroups({
  actionVariables = [],
  types,
}: {
  actionVariables: FieldMappingGroup[];
  types?: FieldType[];
}): FieldMappingGroup[] {
  return useMemo(() => {
    if (!types) {
      return actionVariables;
    }

    return actionVariables
      .map((variable) => {
        const filteredFields = variable.fields.filter((fieldItem) => {
          return fieldItem.type != null && types.includes(fieldItem.type);
        });

        return {
          ...variable,
          fields: filteredFields,
        };
      })
      .filter((group) => group.fields.length > 0);
  }, [actionVariables, types]);
}

const extendFieldMappingGroups = (
  fieldMappingGroups: FieldMappingGroup[],
  actionVariables: ActionVariable[]
): FieldMappingGroup[] => {
  for (const actionVariable of actionVariables) {
    const keyFieldPairs = Object.entries(actionVariable.fields);
    if (keyFieldPairs.length > 0) {
      const fields: FieldMappingFieldItem[] = keyFieldPairs.map(
        ([key, { label, type, options, variable }]) => ({
          source: 'action',
          field: key,
          label,
          type,
          variable,
          action: actionVariable.shortId,
          ...(options && { options }),
        })
      );

      const groupIcon = <ActionIcon action={actionVariable.action} color={fieldMappingIconColor} />;
      fieldMappingGroups.push({
        id: actionVariable.shortId,
        action: actionVariable.action,
        description: actionVariable.description,
        icon: groupIcon,
        label: actionVariable.label ?? getActionLabel(actionVariable.action),
        fields,
      });
    }
  }

  return fieldMappingGroups;
};

const extractFormFields = (
  formFields: FormFieldType[],
  journeyAttributeVariables: FieldMappingFieldItem[],
  companyAttributeVariables: FieldMappingFieldItem[]
): FieldMappingFieldItem[] => {
  const fields: FieldMappingFieldItem[] = [];

  for (const field of formFields) {
    if (field.kind === 'section') {
      fields.push(
        ...extractFormFields(field.fields, journeyAttributeVariables, companyAttributeVariables)
      );
      continue;
    }

    if ('label' in field && typeof field.label === 'string') {
      if ('journeyAttributeId' in field && field.journeyAttributeId) {
        const attributeVariable = journeyAttributeVariables.find((attr) =>
          attr.source.endsWith(`__${field.journeyAttributeId}`)
        );
        if (attributeVariable) {
          fields.push({
            source: attributeVariable.source,
            field: attributeVariable.field,
            label: field.label,
            type: attributeVariable.type,
          });
        }
        continue;
      }

      if ('companyAttributeId' in field && field.companyAttributeId) {
        const attributeVariable = companyAttributeVariables.find((attr) =>
          attr.source.endsWith(`__${field.companyAttributeId}`)
        );
        if (attributeVariable) {
          fields.push({
            source: attributeVariable.source,
            field: attributeVariable.field,
            label: field.label,
            type: attributeVariable.type,
          });
        }
        continue;
      }
    }
  }

  return fields;
};

const formHasPhoneField = (formFields: FormFieldType[]): boolean => {
  for (const field of formFields) {
    if (field.kind === 'section') {
      if (formHasPhoneField(field.fields)) return true;
      continue;
    }
    if (field.kind === 'input-phone' && 'companyAttributeId' in field && field.companyAttributeId) {
      return true;
    }
  }
  return false;
};

const createExternalFormGroups = (
  journeyForms: JourneyForm[],
  journeyAttributeVariables: FieldMappingFieldItem[],
  companyAttributeVariables: FieldMappingFieldItem[],
  defaultFormFields: FieldMappingFieldItem[],
  phoneField: FieldMappingFieldItem
): FieldMappingGroup[] => {
  return journeyForms
    .map((form) => {
      const formSpecificFields = extractFormFields(
        form.fieldsConfig?.fields ?? [],
        journeyAttributeVariables,
        companyAttributeVariables
      );

      const hasPhone = formHasPhoneField(form.fieldsConfig?.fields ?? []);
      const fields = hasPhone
        ? [...defaultFormFields, phoneField, ...formSpecificFields]
        : [...defaultFormFields, ...formSpecificFields];

      return {
        id: `externalForm_${form.shortId}`,
        icon: <IconForm color="elementBlue100" />,
        label: form.name,
        fields,
      } as FieldMappingGroup;
    })
    .filter((group): group is FieldMappingGroup => group !== null);
};

export const useVariableLocale = () => useScopedTranslate(variableLocale).t;

export const useVariableGroups = ({
  actionVariables = [],
  journeyId,
  types = undefined,
  excludedTypes = ['array'],
}: {
  actionVariables: ActionVariable[] | undefined;
  journeyId: string;
  types?: FieldType[] | undefined;
  excludedTypes?: FieldType[] | undefined;
}): FieldMappingGroup[] => {
  const { t } = useScopedTranslate(variableLocale);
  const journeyQuery = useJourney(journeyId);
  const companytravellerAttributeVariablesQuery = useCompanyTravellerAttributeTemplateVariables();
  const journeyFormsQuery = useJourneyFormsList(journeyId);

  const travellerJourneyAttributeVariables = journeyQuery.data?.attributeVariables ?? [];
  const companytravellerAttributeVariables = companytravellerAttributeVariablesQuery.data ?? [];
  const journeyForms = journeyFormsQuery.data ?? [];
  const travellerJourneyAttributeFiltered = travellerJourneyAttributeVariables.filter(
    (attr) => !excludedTypes.includes(attr.type as FieldType)
  );

  const defaultFormFields: FieldMappingFieldItem[] = [
    { label: t('core.fullName'), source: 'company_traveller', field: 'name', type: 'text' },
    { label: t('core.email'), source: 'traveller', field: 'email', type: 'email' },
  ];

  const phoneField: FieldMappingFieldItem = {
    label: t('core.phone'),
    source: 'company_traveller',
    field: 'phone',
    type: 'phone',
  };

  const externalFormGroups = createExternalFormGroups(
    journeyForms,
    travellerJourneyAttributeVariables as FieldMappingFieldItem[],
    companytravellerAttributeVariables as FieldMappingFieldItem[],
    defaultFormFields,
    phoneField
  );

  const baseVariableGroups = [
    {
      id: 'traveller',
      icon: <IconApplicants color={fieldMappingIconColor} />,
      label: t('traveller'),
      fields: [
        {
          label: t('core.fullName'),
          source: 'company_traveller',
          field: 'name',
          type: 'text',
        },
        {
          label: t('core.firstName'),
          source: 'company_traveller',
          field: 'first_name',
          type: 'text',
        },
        {
          label: t('givenName'),
          source: 'company_traveller',
          field: 'first_and_middle_name',
          type: 'text',
          tooltip: t('givenNameTooltip'),
        },
        {
          label: t('core.lastName'),
          source: 'company_traveller',
          field: 'last_name',
          type: 'text',
        },
        { label: t('core.email'), source: 'traveller', field: 'email', type: 'email' },
        ...(phoneField ? [phoneField] : []),
        ...companytravellerAttributeVariables,
        { type: 'separator', source: 'separator', field: 'traveller_ids' },
        {
          label: t('travellerId'),
          source: 'traveller_journey',
          field: 'id',
          type: 'text',
          icon: <IconId size={16} color="elementPurple80" />,
        },
        {
          label: t('travellerUrl'),
          source: 'traveller_journey',
          field: 'url',
          type: 'text',
          icon: <IconTextFormatLink size={16} color="elementPurple80" />,
        },
      ],
    },
    ...(travellerJourneyAttributeFiltered.length > 0
      ? [
          {
            id: 'journeyFields',
            icon: <IconApplicants color={fieldMappingIconColor} />,
            label: t('journeyFields'),
            fields: travellerJourneyAttributeVariables,
          },
        ]
      : []),
    ...externalFormGroups,
    {
      id: 'journey',
      icon: <IconJourneyItem color={fieldMappingIconColor} />,
      label: t('journey'),
      fields: [{ label: t('journeyTitle'), source: 'journey', field: 'title', type: 'text' }],
    },
    {
      id: 'company',
      icon: <IconCompany color={fieldMappingIconColor} />,
      label: t('company'),
      fields: [
        {
          label: t('companyName'),
          source: 'company',
          field: 'name',
          type: 'text',
          icon: <IconCompany color={fieldMappingIconColor} />,
        },
      ],
    },
  ] as FieldMappingGroup[];

  const baseVariableGroupsMapped = baseVariableGroups.map((group) => ({
    ...group,
    fields: group.fields.map((field) => ({
      ...field,
      icon:
        field.icon ??
        (['name', 'first_name', 'first_and_middle_name', 'last_name'].includes(field.field) ? (
          <IconPersonDetails color={fieldMappingIconColor} />
        ) : undefined),
    })),
  }));

  return extendFieldMappingGroups(baseVariableGroupsMapped, actionVariables)
    .map((group) => ({
      ...group,
      fields: group.fields
        .filter((field) => {
          if (!field.type || field.type === 'separator') {
            return true;
          }
          const isExcludedType = excludedTypes.includes(field.type);
          const isExplicitlyIncluded = types?.includes(field.type);

          return types
            ? (!isExcludedType || isExplicitlyIncluded) && types.includes(field.type)
            : !isExcludedType;
        })
        .filter((field, index, array) => {
          return field.type !== 'separator' || (index !== 0 && index !== array.length - 1);
        }),
    }))
    .filter((group) => group.fields.length > 0);
};

type DescriptionFormFieldProps = {
  defaultValue?: string;
};

export const DescriptionFormField = ({ defaultValue }: DescriptionFormFieldProps) => {
  const { t } = useScopedTranslate(variableLocale);
  const initalValue = useRef(defaultValue ?? '');

  const { value, ...formProps } = useFormField({
    id: 'description',
    label: t('description'),
    validators: [],
    defaultValue: initalValue.current,
  });

  return (
    <TextAreaDynamic
      {...formProps}
      data-cy="description-field"
      defaultValue={initalValue.current}
      placeholder={t('addDescription')}
      maxLength={1000}
    />
  );
};

type ActionRemoveButtonProps = Pick<IconButtonProps, 'disabled' | 'tooltip'> & {
  action: JourneyActionBasic;
};

export const nodeToActionBasic = (node: JourneyNode | TravellerJourneyNode): ActionBasic => ({
  action: node.type as ActionType,
  shortId: node.id,
  description: node.data?.description,
});

export const ActionRemoveButton = ({ action, ...rest }: ActionRemoveButtonProps) => {
  const { deleteAction } = useDeleteJourneyAction();

  return (
    <IconButton
      icon={<IconTrash />}
      variant="ghost-purple"
      stopPropagation
      onClick={() => deleteAction(action)}
      {...rest}
    />
  );
};

type ActionDuplicateButtonProps = {
  journeyActionShortId: string;
};

export const ActionDuplicateButton = ({ journeyActionShortId }: ActionDuplicateButtonProps) => {
  const { t } = useScopedTranslate(variableLocale);
  const duplicateMutation = useDuplicateJourneyAction();
  useRotatingLoadingMessages({
    isEnabled: duplicateMutation.isLoading,
    firstMessage: t('duplicatingAction'),
  });

  return (
    <IconButton
      variant="ghost-purple"
      icon={<IconCopy />}
      onClick={() => duplicateMutation.mutate(journeyActionShortId)}
      disabled={duplicateMutation.isLoading}
    />
  );
};

type ActionHeaderProps = Omit<SimpleHeaderProps, 'title' | 'icon' | 'iconColor'> & {
  actionType?: ACTION_TYPE;
  title?: string;
  icon?: typeof BaseIcon;
  iconColor?: Color;
  customIcon?: ReactElement<IconProps>;
  journeyActionId?: string;
};

const SidebarPresenceIndicator = ({ actionId }: { actionId: string }) => {
  const { actionEditors, activeUsers, currentUserId } = usePresence();

  const editorUserIds = actionEditors.get(actionId);
  const editors = editorUserIds
    ? activeUsers.filter((user) => editorUserIds.has(user.userId) && user.userId !== currentUserId)
    : [];

  return (
    <UserAvatarList
      users={editors}
      maxVisible={MAX_VISIBLE_PRESENCE_AVATARS}
      tooltipPlacement="bottom"
    />
  );
};

export const ActionHeader: React.FC<ActionHeaderProps> = ({
  children,
  customIcon,
  journeyActionId,
  actionType,
  title,
  icon,
  iconColor,
  ...rest
}) => {
  const { activeId } = useSidebarState();
  const { sendActionOpened, sendActionClosed } = usePresence();

  useEffect(() => {
    if (journeyActionId && activeId === journeyActionId) {
      sendActionOpened(journeyActionId);
      return () => {
        sendActionClosed(journeyActionId);
      };
    }
  }, [journeyActionId, activeId, sendActionOpened, sendActionClosed]);

  return (
    <Sidebar.SimpleHeader
      {...rest}
      title={title ?? (actionType ? getActionLabel(actionType) : '')}
      icon={icon ?? (actionType ? getActionIcon(actionType) : undefined)}
      iconColor={iconColor ?? (actionType ? getActionIconColor(actionType) : undefined)}
      customIcon={customIcon}
      onClose={hideSidebar}
    >
      {journeyActionId && activeId === journeyActionId && (
        <SidebarPresenceIndicator actionId={journeyActionId} />
      )}
      {children}
    </Sidebar.SimpleHeader>
  );
};

type ActionSidebarLoadingProps = SidebarProps & {
  actionType?: ACTION_TYPE;
  title?: string;
  icon?: typeof BaseIcon;
  iconColor?: Color;
  customIcon?: ReactElement<IconProps>;
};

const LoadingContainer = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '60vh',
});

export const ActionSidebarLoading = ({
  title,
  icon,
  iconColor,
  customIcon,
  ...props
}: ActionSidebarLoadingProps) => (
  <Sidebar>
    <ActionHeader
      title={title}
      icon={icon}
      iconColor={iconColor}
      customIcon={customIcon}
      {...props}
    />
    <Sidebar.Content>
      <SidebarContentLoadingSpinner />
    </Sidebar.Content>
  </Sidebar>
);

export const SidebarContentLoadingSpinner = () => {
  return (
    <LoadingContainer>
      <RelativeSpinner />
    </LoadingContainer>
  );
};

type ActionFooterProps<T> = {
  action: JourneyActionBasic & { isConfigured: boolean };
  isDisabled: boolean;
  form: FormInstance<T>;
  onPreview?: () => void;
  onCancel?: () => void;
  hideActionButtons?: boolean;
  children?: ReactNode;
};

export const ActionFooter = <T extends FormDataType>({
  action,
  isDisabled,
  form: Form,
  onPreview,
  onCancel,
  hideActionButtons,
  children,
}: ActionFooterProps<T>) => {
  const { t } = useScopedTranslate(variableLocale);

  return (
    <Sidebar.Footer>
      <Stack orientation="horizontal" justify="space-between">
        <Stack align="center" space={2} orientation="horizontal">
          {!hideActionButtons && (
            <>
              <ActionRemoveButton action={action} />
              <ActionDuplicateButton journeyActionShortId={action.shortId} />
            </>
          )}
          {onPreview && action.isConfigured && (
            <IconButton variant="ghost-purple" icon={<IconOpenPreview />} onClick={onPreview} />
          )}
          {children}
        </Stack>
        <Stack orientation="horizontal" space={2}>
          <Button variant="secondary" text={t('core.cancel')} onClick={onCancel ?? hideSidebar} />
          <Form.Submit
            data-cy="action-save-btn"
            variant="primary-blue"
            text={t('core.save')}
            isDisabled={isDisabled}
          />
        </Stack>
      </Stack>
    </Sidebar.Footer>
  );
};

const variableLocale = {
  en: {
    traveller: 'Traveller',
    travellerId: 'Traveller • ID',
    travellerUrl: 'Traveller • URL',
    journey: 'Journey details',
    journeyTitle: 'Journey title',
    journeyFields: 'Journey fields',
    company: 'Company',
    companyName: 'Company name',
    description: 'Description',
    addDescription: 'Add description',
    confirmDescriptionEmpty: 'Are you sure you want to delete this action?',
    confirmDescription:
      'Moving this action will break connected values between the actions below.\n\nAre you sure you want to delete this action?',
    confirmActionLabel: 'Connected actions',
    removingAction: 'Removing...',
    duplicatingAction: 'Duplicating...',
    givenName: 'Given name',
    givenNameTooltip: 'First and middle name(s) (if provided)',
  },
};

export const GenericActionCard = ({ action }: { action: ActionBasic }) => {
  return (
    <NodeActionCardContainer>
      <ActionCard skipHoverState>
        <ActionCardHeader
          icon={<ActionIcon action={action.action} />}
          label={getActionLabel(action.action)}
        />
        <ActionCardContent>
          <ActionCardDescription text={action.description ?? ''} />
        </ActionCardContent>
      </ActionCard>
    </NodeActionCardContainer>
  );
};

export type ActionBasic = {
  action: BASE_ACTION_TYPE;
  description?: string;
  shortId: string;
};

export type JourneyActionBasic = Pick<JourneyAction, 'action' | 'shortId' | 'description'>;

const NodeActionCardContainer = styled.div({
  width: '100%',
  '> div': {
    width: '100% !important',
  },
});

type NodeProp = {
  node: JourneyNode<BASE_ACTION_TYPE> | TravellerJourneyNode<BASE_ACTION_TYPE>;
};

type JourneyIconButtonProps = XOR<NodeProp, { icon: ReactElement<IconProps> }> & {
  color: Color;
  isSelectable?: boolean;
  cursor?: 'pointer' | 'default';
  onClick?: () => void;
};

export const JourneyIconButton = ({
  node,
  icon,
  color,
  isSelectable = false,
  cursor,
  onClick,
}: JourneyIconButtonProps) => {
  const isActive = useIsSidebarActive(node?.id ?? '');
  const finalIcon = icon ?? <ActionIcon action={node?.type} color={color} />;

  return (
    <StyledJourneyIconButton
      $isActive={isActive}
      $isSelectable={isSelectable}
      $cursor={cursor}
      onClick={onClick}
    >
      {finalIcon}
    </StyledJourneyIconButton>
  );
};

const StyledJourneyIconButton = styled.div<{
  $isActive?: boolean;
  $isSelectable: boolean;
  $cursor?: 'pointer' | 'default';
}>(({ $isActive, $isSelectable, $cursor }) => ({
  transition: 'border-color 0.2s ease-out, border-width 0.2s ease-out, box-shadow 0.2s ease-out',
  alignItems: 'center',
  backgroundColor: theme.color.backgroundLightDark,
  borderWidth: pxToRem(1),
  borderStyle: 'solid',
  borderColor: theme.color.border20,
  borderRadius: pxToRem(8),
  display: 'flex',
  height: pxToRem(64),
  justifyContent: 'center',
  width: pxToRem(64),
  cursor: $cursor,

  ...($isSelectable && {
    boxShadow: theme.shadow.focus,
    '--shadow-focus-width': pxToRem(8),
    '--shadow-focus-gap': 0,

    ':hover': {
      ...(!$isActive && {
        '--shadow-focus-color': theme.color.uiTrans10,
      }),
    },
  }),

  ...($isActive && {
    borderWidth: pxToRem(2),
    borderColor: theme.color.elementBlue100,
    '--shadow-focus-color': theme.color.blueTrans20,
  }),
}));

export const createActionForm = <T = any,>() =>
  createForm<T>({ onSubmitSuccess: hideModal, validationMode: 'onSubmit' });

export const useCreateActionFormStore = (
  initialState?: Parameters<typeof useCreateFormStore>[0]
) => {
  return useCreateFormStore({ validationMode: 'onSubmit', ...initialState });
};
